import logger from "../../utils/logger.js";
import getNewProp from "../../utils/getNewProp.js";
import apiRequest from "../../utils/ApiRequest/apiRequest.js";
import queryString from "query-string";
import getMailDeleteHashId from "helpers/getMailDeleteHashId.ts";
import { updater, responseUpdater } from "../../utils/updater.js";
import removeMailDeleteHashId from "helpers/removeMailDeleteHashId.ts";
import { initialState } from "./mailContext.js";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
} from "utils/globalLocalStorage.js";
import { sortingFiles } from "utils/gridHelpers.js";

const galleryFactory = ({ state, setState }) => {
  const updateState = updater(setState);

  const cleanUpFunction = () => {
    setState(initialState);
  };

  // ------- HELPERS ----- //
  const updateNewMailProp = (response) => {
    if (response.status === "success") {
      const newProp = getNewProp(response.data, state.userVideoToken);
      updateState((draft) => {
        draft.metaData.CSRFToken = newProp;
      });
    }
  };

  // ------- ACTIONS ----- //
  const shareMail = async ({ body, folderId, token }) => {
    const params = {
      MailId: body.collectionId ? body.collectionId : body.mailId,
      isCollection: !!body.collectionId,
      isFromCollection: state.isCollection,
      SenderEmail: body.ShareToEmailSenderTitle,
      Recipients: body.ShareToEmailRecipientTitle,
      Body: body.ShareToEmailBodyTitle,
      Subject: body.ShareToEmailSubjectTitle,
      FilesList: body.FilesList,
      folderPathId: parseInt(folderId),
      CSRFToken: token || getMailMetaData().CSRFToken,
      RecaptchaResponse: body.recaptchaResponse,
      captchaServiceName: body?.captchaServiceName,
    };
    const response = await apiRequest(
      "mails",
      "ReactShareMail",
      params,
      "POST"
    );
    if (!token) {
      updateNewMailProp(response);
    }
    return response;
  };

  const startDownload = async ({
    fileId = null,
    folderId = null,
    selectedFiles,
    isProxySuccess,
    culture,
    mailId,
    uid,
    recaptchaResponse,
  }) => {
    const stringLlist =
      fileId === null &&
      selectedFiles.length < 1 &&
      Object.keys(state.filesData).length === 1 &&
      state.metaData.filesCount === 1
        ? Object.keys(state.filesData).toString()
        : fileId !== null
        ? fileId
        : selectedFiles.toString();

    const listType =
      fileId !== null ||
      selectedFiles.length > 0 ||
      (Object.keys(state.filesData).length === 1 &&
        state.metaData.filesCount === 1)
        ? "multi"
        : "zip";

    const params = {
      folderPathId: folderId,
      encryptedMailId: mailId,
      fileList: stringLlist,
      downloadType: listType,
      password: getLocalStorage(`mailPassword-${mailId}`) || null,
      isCollection: state.isCollection,
      culture: culture,
      isProxySuccess: isProxySuccess,
      uid: uid || null,
      recaptchaResponse,
    };
    const response = await apiRequest(
      "mails",
      "ReactGetDownloadLink",
      params,
      "POST"
    );

    if (response.status === "success") {
      updateState((draft) => {
        draft.metaData.DownloadsCount = state.metaData.DownloadsCount + 1;
      });
      const link = `${response.data}`;
      window.location.href = link;
    }
    return response;
  };

  // ------- PARAMS ------ //
  const getMailId = () => state.mailId;

  const getFileId = () => state.fileId;

  const getMailMetaData = () => state.metaData;

  const isMailInfoReady = () => {
    return !!state.metaData.shortUrl;
  };
  const getPaymentData = () =>
    state.payment && state.payment.Price ? state.payment : false;

  const getIsDisplayAllowed = () => state.isDisplayAllowed;

  const setIsDisplayAllowed = (status) => {
    updateState((draft) => {
      draft.isDisplayAllowed = status;
    });
  };

  const isDownloadAllowed = () => {
    return !state.payment || (state.payment && state.payment.AllowDownload);
  };

  const setFileId = (fileId) => {
    updateState((draft) => {
      draft.fileId = fileId || null;
    });
  };

  const setMailParams = ({
    mailId = null,
    fileId = null,
    isCollection = false,
  }) => {
    updateState((draft) => {
      draft.isCollection = isCollection;
      draft.mailId = mailId;
      draft.fileId = fileId;
    });
  };

  const setDeleteParam = (param) => {
    updateState((draft) => {
      draft.deleteParam = param;
    });
  };

  const getDeleteParam = () => state.deleteParam;

  const getIsCollection = () => state.isCollection;

  // ------- MAIL-INFO SECTION ----- //
  const getMailSecurity = () => (state.security ? state.security : null);

  const getAlertToUser = () => state.alertToUser;

  const setAlertToUser = (obj) => {
    updateState((draft) => {
      draft.alertToUser = [obj];
    });
  };

  const setUserVideoToken = (response) => {
    const value = response;

    updateState((draft) => {
      draft.userVideoToken = value;
    });
  };

  const setSecurity = (newSecurityObj) => {
    updateState((draft) => {
      draft.security = newSecurityObj;
    });
  };

  const getUserPasswordInput = () => state.userPasswordInput || null;

  const verifyMailPassword = async (password, recaptchaResponse) => {
    const response = await apiRequest(
      "Mails",
      "ReactVerifyMailPassword",
      {
        encryptedMailId: getMailId(),
        password: password,
        isCollection: state.isCollection,
        recaptchaResponse: recaptchaResponse,
        captchaServiceName: "cloudflare",
      },
      "POST"
    );

    if (response.status === "success") {
      if (response.data === true) {
        setLocalStorage(`mailPassword-${getMailId()}`, password);
        updateState((draft) => {
          draft.userPasswordInput = password;
        });
        return {
          status: "success",
          message: "",
        };
      } else {
        return {
          status: "error",
          message: "incorrect-mail-password-response",
        };
      }
    } else {
      logger("mailFactory", "verifyMailPassword", response.data);
      return {
        status: "error",
        message: "incorrect-mail-password-response",
      };
    }
  };

  const getBackgroundCover = () => state.backgroundCover;

  const isSpecificBackground = () => state.isSpecificBackground;

  const isHiddenBanners = () => state.hideMailBanners;

  const setDefaultBackgroundCover = async (image, fileName) => {
    const response = await apiRequest(
      "UserTemplates",
      `ReactAddUserProfileBranding?fileName=${fileName}&encryptedMailId=${
        getMailId() ? getMailId() : 0
      }`,
      image,
      "POST",
      null,
      null,
      null,
      true
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.backgroundCover = response.data.path;
      });
    } else {
      logger("mailFactory", "setDefaultBackgroundCover", response.data);
    }
    return response;
  };

  const getImageSrcForCanvas = async ({ fileId }) => {
    const response = await apiRequest(
      "Mails",
      "ReactGetRealFileSrc",
      {
        encryptedMailId: state.mailId,
        fileId: fileId,
      },
      "POST"
    );
    if (response.status === "success") {
      return response.data;
    }
  };

  const setSpecificMailBrandedCover = async (image, fileName, fileId) => {
    const response = await apiRequest(
      "UserTemplates",
      `ReactSetSpecificMailBrandedCover?&encryptedMailId=${getMailId()}&fileName=${fileName}&fileId=${fileId}`,
      image,
      "POST",
      null,
      null,
      null,
      true
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.backgroundCover = response.data.path;
        draft.isSpecificBackground = true;
      });
    } else {
      logger("mailFactory", "ReactSetMainCoverForMail", response.data);
    }
    return response;
  };

  const resetSpecificMailBrandedCover = async () => {
    const response = await apiRequest(
      "UserTemplates",
      `ReactResetSpecificMailBrandedCover`,
      { encryptedMailId: getMailId() },
      "GET",
      null,
      null,
      null,
      true
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.backgroundCover =
          response.data.toLowerCase() !== "error" ? response.data : null;
        draft.isSpecificBackground = false;
      });
    } else {
      logger("mailFactory", "ReactSetMainCoverForMail", response.data);
    }
    return response;
  };

  const saveMailByCode = async (code) => {
    const params = {
      SaveByCodeEncryptedMailId: getMailId(),
      SaveByCodeCodeKey: code,
    };

    const response = await apiRequest(
      "Mails",
      "ReactClientSaveMailByCode",
      params,
      "POST"
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.metaData.Expiration = response.data;
        // draft.metaData.IsSaved = !getMailMetaData().IsSaved;
      });
    }
    return response;
  };

  const setMailSaveStatus = (isSaved) => {
    updateState((draft) => {
      draft.metaData.isSaved = isSaved;
    });
  };

  const saveMailAsSubscriber = async () => {
    const params = {
      MailId: getMailId(),
      IsUnsave: getMailMetaData().IsSavedByUser,
      isCollection: getIsCollection(),
    };
    const response = await apiRequest(
      "mails",
      "ReactSaveMailAsSubscriber",
      params,
      "POST"
    );
    if (response.status === "success") {
      // if response is date - its mean that its unsave action and noone saved it besides this user
      if (Date.parse(response.data)) {
        updateState((draft) => {
          draft.metaData.IsSaved = false;
          draft.metaData.IsSavedByUser = false;
          draft.metaData.Expiration = response.data;
        });
      } else if (getMailMetaData().IsSavedByUser) {
        updateState((draft) => {
          draft.metaData.IsSavedByUser = false;
        });
      } else {
        updateState((draft) => {
          draft.metaData.IsSaved = true;
          draft.metaData.IsSavedByUser = true;
        });
      }
    }
    return response;
  };

  const setSorting = (data) => {
    if (state.filesLoading) return;
    resetFilesList();
    updateState((draft) => {
      draft.filesLoading = true;
      draft.sorting = { ...data, updated: true };
      draft.pageNumber = 1;
    });
  };

  const getSorting = () => state.sorting;

  const setFolderStructured = (bool) => {
    if (state.filesLoading) return;
    resetFilesList();
    updateState((draft) => {
      draft.folderStructured = bool;
      draft.filesLoading = true;
      draft.pageNumber = 1;
      draft.currentFolder = null;
    });
  };

  const getFolderStructured = () => state.folderStructured;

  const fetchMailPaymentInfo = async () => {
    const response = await apiRequest("Mails", "ReactGetMailPaymentInfo", {
      encryptedMailId: getMailId(),
      isCollection: getIsCollection(),
    });
    if (response.status === "success") {
      updateState((draft) => {
        draft.payment = response.data;
      });
    } else {
      logger("mailFactory", "fetchMailPaymentInfo", response.data);
    }
  };

  const fetchMailInfo = async () => {
    const response = await apiRequest(
      "Mails",
      "ReactGetMailInfo",
      {
        encryptedMailId: getMailId(),
        isCollection: state.isCollection,
      },
      "POST"
    );
    if (response.status === "success") {
      //adding mail password from localstorage, if exists
      response.data.userPasswordInput = getLocalStorage(
        `mailPassword-${getMailId()}`
      );
      updateState((draft) => {
        draft.folderStructured = response.data.MetaData.HasFolders;
      });
      responseUpdater(response, setState, state);
    } else {
      if (response.data) {
        // const errMessage =
        //   typeof response.data === "string"
        //     ? response.data
        //     : JSON.stringify(response.data);
        // if (errMessage.includes("mail is blocked")) {
        //   window.location.href = "/en/user-blocked";
        // }
      }

      logger("mailFactory", "ReactGetMailInfo", response.data);
    }
  };

  const isMailAvailable = () =>
    state.status && state.status.toLowerCase() === "sent";

  const getMailStatus = () =>
    state.status ? state.status.toLowerCase() : null;

  const setMailStatus = (status) =>
    updateState((draft) => {
      draft.status = status;
    });

  const getSender = () => state.sender || {};

  const getSpamStatus = () => state.spamStatus;

  // ------- FILES SECTION ------- //
  const setFilesLoading = (bool) => {
    updateState((draft) => {
      draft.filesLoading = bool;
    });
  };

  const getFilesLoading = () => state.filesLoading;

  // const setDataInUse = (bool) => {
  //   updateState((draft) => {
  //     draft.dataInUse = bool;
  //   });
  // };

  // const getdataInUse = () => state.dataInUse;
  const resetFilesList = () => {
    updateState((draft) => {
      draft.filesData = {};
      draft.filesOrderedList = [];
      draft.preventFetching = false;
    });
  };

  const getFilesList = () => state.filesOrderedList;

  const setFilesList = ({
    filesData,
    foldersData,
    foldersList,
    sorting,
  } = {}) => {
    const data = filesData || state.filesData;
    const list = sortingFiles(
      data,
      foldersData || getFoldersData(),
      foldersList || getFoldersList(),
      sorting ||
        getSorting() || {
          by: "Name",
          asc: true,
        },
      getFolderStructured(),
      state.currentCollection
    );

    updateState((draft) => {
      draft.filesOrderedList = list;
    });
  };

  const setDeleteHashId = (id) => {
    updateState((draft) => {
      draft.deleteHashId = id;
    });
  };

  const removeFiles = async (filesList) => {
    const response = await apiRequest(
      "Mails",
      "DeleteFiles",
      { filesList },
      "DELETE"
    );

    if (response.status === "success") {
      const newFilesData = { ...state.filesData };

      filesList.forEach((fileId) => {
        delete newFilesData[fileId];
      });

      updateState((draft) => {
        draft.metaData.filesCount = response.data.totalFileCount ?? 0;
        draft.metaData.TotalSize = response.data.totalFileSize ?? 0;

        filesList.forEach((fileId) => {
          const folderId = state.filesData[fileId].folderId;
          if (folderId) {
            draft.foldersData[folderId].filesIds = state.foldersData[
              folderId
            ].filesIds.filter((id) => id !== +fileId);
            draft.foldersData[folderId].fileCount =
              state.foldersData[folderId].fileCount - 1;

            draft.foldersData[folderId].nestedFilesSize =
              state.foldersData[folderId].nestedFilesSize -
              state.filesData[fileId].Size;
          }
        });

        draft.filesData = newFilesData;
      });

      return true;
    } else {
      logger("mailFactory", "removeFiles", response.data);
      return false;
    }
  };

  const removeFolder = async (folderId) => {
    const response = await apiRequest(
      "Mails",
      "DeleteFolders",
      { encryptedMailId: state.mailId, foldersList: [folderId] },
      "DELETE"
    );

    if (response.status === "success") {
      // removing folder from foldersData
      const newFoldersData = { ...state.foldersData };
      delete newFoldersData[folderId];

      updateState((draft) => {
        draft.metaData.filesCount =
          response.data.totalFileCount || state.metaData.filesCount;
        draft.metaData.TotalSize =
          response.data.totalFileSize || state.metaData.TotalSize;

        if (state.currentFolder === folderId) {
          draft.currentFolder = state.folderData[folderId].parentId || null;
        }

        draft.foldersData = newFoldersData || {};
        draft.foldersList = sortFoldersFunc(newFoldersData || {});
      });

      fetchFoldersData();

      return true;
    } else {
      return false;
    }
  };

  const fetchMailFiles = async (
    { reset, fetchCallback, mailId } = { reset: false }
  ) => {
    /**
     * INBOX+MAILDATA
     * mailID is passing only from inbox page when opening lightbox
     * in this case we are:
     * - using it within request
     * - overwriting previous mailID
     * - overwriting previous filesData
     *
     *
     * all the other places it works as usual
     */

    //checking if exists structure of url like /gallery/:mailId/delete/:deleteHashId and if exists - taking deleteHashId
    const deleteHashId = getMailDeleteHashId();
    if (deleteHashId && !state.deleteHashId) {
      setDeleteHashId(deleteHashId);

      removeMailDeleteHashId();
    }

    const mailIdToUse = mailId || getMailId();
    if ((!mailIdToUse && !getSorting()) || state.preventFetching) return;
    const password =
      getUserPasswordInput() || getLocalStorage(`mailPassword-${getMailId()}`);

    const isCollection = state.isCollection || state.currentCollection;

    const requestParams = isCollection
      ? {
          model: "Collection",
          request: "ReactGetCollection",
        }
      : {
          model: "Mails",
          request: "ReactGetMailFiles",
        };

    const sortAsc =
      typeof state.sorting?.asc === "boolean" ? state.sorting?.asc : true;

    setFilesLoading(true);

    const response = await apiRequest(
      requestParams.model,
      requestParams.request,
      {
        encryptedMailId: state.currentCollection
          ? state.currentCollection
          : mailIdToUse,
        page: state.pageNumber,
        perPage: 30,
        sortAsc: sortAsc,
        sortBy: state.sorting?.by || "name",
        folderId: state.currentFolder,
        password: password,
        search: state.searchParam,
        deleteHashId: deleteHashId || state.deleteHashId || "",
        contributorsFilter: state.contributorsFilter,
        foldersStructured: isCollection ? false : state.folderStructured,
      },
      "POST"
    );

    if (response.status === "success") {
      const filesList = response.data.files;

      // within this response can appear folders data only in case no-folder structured request
      // cos we need to get only folders relevant to current chunk of files
      updateFoldersData(response.data.folders);

      // detecting empty response
      if (Object.keys(filesList).length === 0) {
        // changing state value to prevernt further fetching
        // it will reseted to false on any changes like search/filter/sort
        updateState((draft) => {
          draft.preventFetching = true;
          draft.filesDataErrorMessage = null; // Clear error message on empty but successful response
        });
        setFilesLoading(false);
        return;
      }

      const newFilesData =
        reset || mailId ? filesList : { ...state.filesData, ...filesList };

      updateState((draft) => {
        draft.filesData = newFilesData;
        draft.searchExpectedFiles = response.data.filesTotalCount;
        draft.filesDataErrorMessage = null; // Clear any previous error message on success
        if (mailId) {
          draft.mailId = mailId;
        }
      });

      fetchCallback && fetchCallback({ success: true });
    } else {
      if (typeof response.data === "string") {
        updateState((draft) => {
          draft.filesDataErrorMessage = response.data;
        });
      }
      if (response.data === "incorrect-password") {
        // remove from localstorage existing password
        removeLocalStorage(`mailPassword-${getMailId()}`);
        updateState((draft) => {
          draft.userPasswordInput = null;
        });
      }
      if (typeof response.data === "string") {
        updateState((draft) => {
          draft.filesDataErrorMessage = response.data;
        });
      }
      if (
        state.currentFolder &&
        !state.foldersData[state.currentFolder].haveFiles
      ) {
        updateState((draft) => {
          draft.filesData = {};
        });
      }
      logger("mailFactory", "ReactGetMailFiles", response.data);
      fetchCallback && fetchCallback({ error: true });
      return response.data;
    }

    setFilesLoading(false);
  };

  const resetPageNumber = () => {
    updateState((draft) => {
      draft.pageNumber = 1;
      draft.preventFetching = false;
    });
  };

  const incPageNumber = () => {
    const newVal = state.pageNumber + 1;
    updateState((draft) => {
      draft.pageNumber = newVal;
    });
  };

  const getPageNumber = () => state.pageNumber;

  const getIsFetchMore = () => {
    // preventing fetching in case previous response was empty files
    if (state.preventFetching) {
      return false;
    }

    const filesCount = Object.keys(getFilesData()).length;

    if (getCurrentCollection()) {
      return (
        state.collectionsData[getCurrentCollection()].fileCount > filesCount
      );
    }

    if (getContributorsFilter().length > 0) {
      const totalLength = Object.entries(getContributorsData()).reduce(
        (acc, [key, value]) => {
          if (getContributorsFilter().includes(key)) {
            return acc + value;
          }
        },
        0
      );
      return totalLength > filesCount;
    }
    if (state.searchParam) {
      return state.searchExpectedFiles > filesCount;
    }

    if (getFolderStructured() && !getCurrentFolder()) {
      return state.foldersData[1]?.filesIds?.length > filesCount;
    }

    const filesLimit = getCurrentFolder()
      ? getCurrentFolderData().filesIds?.length
      : state.currentCollection
      ? state.collectionsData[state.currentCollection].fileCount
      : getMailMetaData().filesCount;

    return filesLimit > filesCount;
  };

  const setSearchParam = (param) => {
    if (param === state.searchParam || state.filesLoading) return;
    resetFilesList();
    updateState((draft) => {
      draft.filesLoading = true;
      draft.searchParam = param;
      draft.pageNumber = 1;
    });
  };

  const getSearchParam = () => state.searchParam;

  const setContributorsFilter = (contributorEmail) => {
    const newContributorsFilter = state.contributorsFilter.includes(
      contributorEmail
    )
      ? state.contributorsFilter.filter((email) => email !== contributorEmail)
      : [...state.contributorsFilter, contributorEmail];

    const isSelectedAll =
      newContributorsFilter.length ===
      Object.keys(getContributorsData()).length;

    resetFilesList();

    updateState((draft) => {
      draft.filesLoading = true;
      draft.contributorsFilter = isSelectedAll ? [] : newContributorsFilter;
      draft.pageNumber = 1;
    });
  };

  const getContributorsFilter = () => state.contributorsFilter;

  const setContributorsData = (data) => {
    updateState((draft) => {
      draft.contributorsData = data;
    });
  };

  const getContributorsData = () => state.contributorsData;

  const getFilesData = () => state.filesData;

  const getFilesDataErrorMessage = () => state.filesDataErrorMessage;

  const requestAccessFromOwner = async () => {
    const response = await apiRequest(
      "MailTemplate",
      "RecipientsOnlyRequestAccess",
      {
        EncryptedMailId: getMailId(),
      },
      "POST"
    );

    if (response.status === "success") {
      return response;
    } else {
      logger("mailFactory", "requestAccessFromOwner", response.data);
    }
  };

  const getIsShowMore = () => {};

  /**
   *
   * 1. Ask all the files from 0-level
   * 2. Ask
   */

  // 0-----------------------------------------
  // 0-----------------------------------------
  // 0-----------------------------------------
  // 0-----------------------------------------
  // 0-----------------------------------------
  const sortFoldersFunc = (fodlersData) => {
    // list of folders ids
    const foldersList = Object.keys(fodlersData);

    // will record sorted folders to this list and return as result of the func
    const resultList = [];

    // function that sorting folders on current nesting level and recodring it to resultList
    const sortNestedFoldersFunc = (incomingSortedList, resultList) => {
      // incomingSortedList - aready sorted list of folders relevant to some nesting level
      incomingSortedList.map((folderId) => {
        //recordint first folder from the list of current nesting level
        resultList.push(folderId);
        //checking if this folder has some nested folders
        if (
          fodlersData[folderId]?.containedFoldersId &&
          fodlersData[folderId]?.containedFoldersId.length > 0
        ) {
          // doing sorting by the name all this nested folders
          const arrayCopy = [...fodlersData[folderId].containedFoldersId];
          const nestedLevelList = arrayCopy.sort((a, b) => {
            const valueA = fodlersData[a]?.folderName?.toLowerCase();
            const valueB = fodlersData[b]?.folderName?.toLowerCase();
            if (valueA < valueB) {
              return -1;
            }
            if (valueA > valueB) {
              return 1;
            }
            // names must be equal
            return 0;
          });
          //recursively using this function with folowwing nesting folders
          sortNestedFoldersFunc(nestedLevelList, resultList);
        }
      });
    };

    if (foldersList.length > 1) {
      //first step taking all the folders from top level - parentId === 0
      const tempList = foldersList.filter((folderId) => {
        return fodlersData[folderId].parentId === 0;
      });

      //now sorting all this top-level folder by name a-z
      const highLevelList = tempList.sort((a, b) => {
        const valueA = fodlersData[a].folderName?.toLowerCase();
        const valueB = fodlersData[b].folderName?.toLowerCase();
        if (valueA < valueB) {
          return -1;
        }
        if (valueA > valueB) {
          return 1;
        }
        // names must be equal
        return 0;
      });

      // using sortFunction to sort nested levels of folders and using in recursive till the end of nesting levels
      sortNestedFoldersFunc(highLevelList, resultList);
      return resultList;
    }

    return [];
  };
  // 0-----------------------------------------
  // 0-----------------------------------------
  // 0-----------------------------------------
  // 0-----------------------------------------

  // ------- FOLDERS SECTION ------- //
  const updateFoldersData = (responseData) => {
    if (responseData && Object.keys(responseData).length > 0) {
      const newFoldersData = { ...state.foldersData };
      Object.entries(responseData).forEach(([folderId, folderData]) => {
        if (
          !newFoldersData[folderId] ||
          responseData[folderId].containedFoldersId !== null
        ) {
          newFoldersData[folderId] = folderData;
        }
      });

      updateState((draft) => {
        draft.foldersData = newFoldersData || {};
        draft.foldersList = sortFoldersFunc(newFoldersData || {});
      });
    }
  };

  const fetchFoldersData = async () => {
    //checking if exists structure of url like /gallery/:mailId/delete/:deleteHashId and if exists - taking deleteHashId
    const deleteHashId = getMailDeleteHashId();
    if (deleteHashId && !state.deleteHashId) {
      setDeleteHashId(deleteHashId);

      // removeMailDeleteHashId();
    }

    const password =
      getUserPasswordInput() || getLocalStorage(`mailPassword-${getMailId()}`);
    const response = await apiRequest("Mails", "ReactGetMailFolders", {
      encryptedMailId: getMailId(),
      folderId: getCurrentFolder() || null,
      deleteHashId: deleteHashId || state.deleteHashId || "",
      password: password,
    });
    if (response.status === "success") {
      updateFoldersData(response.data);
    } else {
      logger("mailFactory", "fetchFoldersData", response.data);
    }
  };

  //getting current list of folders.
  const getAvailableFoldersList = () => {
    const unsoretedFoldersList = state.currentFolder
      ? state.foldersData[state.currentFolder].containedFoldersId
        ? state.foldersData[state.currentFolder].containedFoldersId
        : []
      : state.foldersData[1].containedFoldersId || [];

    const filteredFoldersList =
      state.contributorsFilter?.length === 0
        ? unsoretedFoldersList
        : unsoretedFoldersList.filter(
            (folder) =>
              !state.foldersData[folder].contributorName ||
              state.contributorsFilter.includes(
                state.foldersData[folder].contributorName
              )
          );

    const sortedList = filteredFoldersList
      .filter((id) => state.foldersData[id] || state.foldersData[id + ""])
      .slice()
      .sort((a, b) => {
        const valueA = state.foldersData[a]?.folderName.toLowerCase();
        const valueB = state.foldersData[b]?.folderName.toLowerCase();
        if (valueA < valueB) {
          return -1;
        }
        if (valueA > valueB) {
          return 1;
        }
        // names must be equal
        return 0;
      });

    return sortedList;
  };

  //getting the current path as array
  const getPathToFolder = (folderId = state.currentFolder) => {
    let folder = state.foldersData[folderId];
    let folderIteration = folderId;
    let result = [];

    if (folder) {
      while (folderIteration) {
        result.unshift({ id: folder.id, name: folder.folderName });
        folder = state.foldersData[state.foldersData[folderIteration].parentId];
        folderIteration =
          state.foldersData[folderIteration].parentId !== 1
            ? state.foldersData[folderIteration].parentId
            : false;
      }
    }
    return result;
  };

  //getting the structure of the folders in current mail
  const getFoldersData = () => state.foldersData;

  const getFoldersList = () => state.foldersList;

  //getting the current folder the user is viewing
  const getCurrentFolder = () => state.currentFolder;

  const getCurrentFolderData = () =>
    state.foldersData
      ? state.foldersData[state.currentFolder ? state.currentFolder : 0]
      : null;

  //setting the current folder path the user is viewing
  const setCurrentFolder = (folderId) => {
    const folderIdInt = parseInt(folderId);
    if (state.filesLoading || folderId === state.currentFolder) return;
    const sortingObj = { ...state.sorting };
    updateState((draft) => {
      draft.filesLoading = true;
      draft.currentFolder = folderIdInt;
      draft.currentCollection = null;
      draft.pageNumber = 1;
      draft.sorting = sortingObj;
      draft.filesData = {};
      draft.filesOrderedList = [];
    });
  };

  // ------- COLLECTIONS SECTION ------- //
  const fetchCollectionsData = async () => {
    const response = await apiRequest(
      "Collection",
      "ReactGetAllCollectionsForMail",
      {
        encryptedMailId: getMailId(),
      },
      "GET"
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.collectionsData = response.data || {};
      });
    } else {
      logger("mailFactory", "fetchCollectionsData", response.data);
    }
  };

  const addCollection = async (value, selectedFiles = [], folderId = null) => {
    const searchParams = queryString.parse(window.location.search);
    const response = await apiRequest(
      "Collection",
      "ReactCreateCollection",
      {
        folderPathId: parseInt(folderId),
        selectedItems: selectedFiles,
        isCollection: getIsCollection(),
        collectionName: value,
        encryptedMailId: getMailId(),
        uid: searchParams.uid || null,
      },
      "POST"
    );
    if (response.status === "success") {
      updateState((draft) => {
        const imagesWithinArray = selectedFiles.filter(
          (item) => state.filesData[item].Type === "Image"
        );
        draft.collectionsData = {
          [response.data.encryptedCollectionId]: {
            ...response.data,
            collectionCover:
              response.data.collectionCover ||
              (imagesWithinArray.length < 1
                ? null
                : state.filesData[imagesWithinArray[0]].ThumbSrc),
          },
          ...state.collectionsData,
        };
      });
      return response;
    } else {
      logger("mailFactory", "addCollection", response.data);
      return response;
    }
  };

  const editCollectionName = async (collectionId, value) => {
    const response = await apiRequest("Collection", "ReactEditCollectionName", {
      encryptedCollectionId: collectionId,
      newName: value,
    });
    if (response.status === "success") {
      updateState((draft) => {
        draft.collectionsData[collectionId].collectionName = value;
      });
      return response;
    } else {
      logger("mailFactory", "editCollectionName", response.data);
    }
  };

  const deleteCollection = async (collectionId) => {
    const response = await apiRequest(
      "Collection",
      "ReactDeleteCollection ",
      {
        encryptedCollectionId: collectionId,
        encryptedMailId: getMailId(),
        isCollection: true,
      },
      "POST"
    );
    if (response.status === "success") {
      if (state.currentCollection === collectionId) {
        setCurrentCollection(null);
      }
      updateState((draft) => {
        delete draft.collectionsData[collectionId];
      });
      return response;
    } else {
      logger("mailFactory", "deleteCollection", response.data);
    }
  };

  const isFileInCollection = (collectionId, file) => {
    return state.collectionsData[collectionId].selectedFileIds.includes(file);
  };

  const addFilesToCollection = async (collectionId, filesArray) => {
    const notInCollectionArray = filesArray.filter(
      (item) =>
        !state.collectionsData[collectionId].selectedFileIds.includes(item)
    );
    if (notInCollectionArray.length < 1) {
      return;
    }
    const response = await apiRequest(
      "Collection",
      "ReactAddFilesToCollection",
      {
        encryptedCollectionId: collectionId,
        encryptedMailId: getMailId(),
        selectedItems: notInCollectionArray,
        isCollection: getIsCollection(),
      },
      "POST"
    );
    if (response.status === "success" && response.data) {
      const imagesWithinArray = notInCollectionArray.filter(
        (item) => state.filesData[item].Type === "Image"
      );
      updateState((draft) => {
        draft.collectionsData[collectionId].selectedFileIds = [
          ...draft.collectionsData[collectionId].selectedFileIds,
          ...notInCollectionArray,
        ];
        draft.collectionsData[collectionId].fileCount +=
          notInCollectionArray.length;
        draft.collectionsData[collectionId].collectionCover =
          draft.collectionsData[collectionId].collectionCover ||
          imagesWithinArray.length < 1
            ? draft.collectionsData[collectionId].collectionCover
            : state.filesData[imagesWithinArray[0]].ThumbSrc;
      });
    } else {
      logger("mailFactory", "addFilesToCollection", response.data);
    }
  };

  const removeFilesFromCollection = async (collectionId, filesArray) => {
    const response = await apiRequest(
      "Collection",
      "ReactDeleteFileFromCollection ",
      {
        encryptedCollectionId: collectionId,
        encryptedMailId: getMailId(),
        selectedItems: filesArray,
      },
      "POST"
    );
    if (response.status === "success" && response.data) {
      updateState((draft) => {
        draft.collectionsData[collectionId].selectedFileIds =
          state.collectionsData[collectionId].selectedFileIds.filter((item) => {
            return !filesArray.includes(item);
          });
        draft.collectionsData[collectionId].fileCount -= filesArray.length;
        draft.collectionsData[collectionId].collectionCover = null;
      });
    } else {
      logger("mailFactory", "removeFilesFromCollection", response.data);
    }
  };

  const getCollectionsData = () => state.collectionsData;

  const getCollectionsList = () => Object.keys(state.collectionsData);

  const getCurrentCollection = () => state.currentCollection;

  const setCurrentCollection = (collectionId) => {
    if (state.filesLoading) return;
    if (state.collectionsData[collectionId]?.fileCount < 1) return;
    const sortingObj = { ...state.sorting };
    updateState((draft) => {
      draft.filesLoading = true;
      draft.pageNumber = 1;
      draft.currentFolder = null;
      draft.sorting = sortingObj;
      draft.currentCollection =
        collectionId === state.currentCollection ? null : collectionId;
    });
  };

  const setMetaDataProp = (prop, value) => {
    updateState((draft) => {
      draft.metaData[prop] = value;
    });
  };

  const setFilesData = (data) => {
    updateState((draft) => {
      draft.filesData = data;
    });
  };

  const setFilePlaying = (fileId) => {
    updateState((draft) => {
      draft.currentFilePlaying = fileId;
    });
  };

  const getFilePlaying = () => state.currentFilePlaying;

  const setReuploadingMail = (mailId) => {
    updateState((draft) => {
      draft.reuploadingMail = mailId;
    });
  };

  const getReuploadingMail = () => state.reuploadingMail;

  return {
    setFilesData,
    cleanUpFunction,
    setUserVideoToken,
    getFilePlaying,
    setFilePlaying,
    //actions
    shareMail,
    getIsCollection,
    startDownload,
    requestAccessFromOwner,
    //params
    getFileId,
    getMailId,
    setFileId,
    setMailParams,
    setDeleteParam,
    getDeleteParam,
    setDeleteHashId,
    //security
    getPaymentData,
    isDownloadAllowed,
    verifyMailPassword,
    getMailSecurity,
    //mail-info
    setSecurity,
    isMailInfoReady,
    setMailSaveStatus,
    getAlertToUser,
    setAlertToUser,
    getMailMetaData,
    getUserPasswordInput,
    getSpamStatus,
    getBackgroundCover,
    isSpecificBackground,
    isHiddenBanners,
    setSpecificMailBrandedCover,
    setDefaultBackgroundCover,
    resetSpecificMailBrandedCover,
    saveMailAsSubscriber,
    saveMailByCode,
    setMetaDataProp,
    fetchMailInfo,
    fetchMailPaymentInfo,
    isMailAvailable,
    getMailStatus,
    setMailStatus,
    getSender,
    getIsDisplayAllowed,
    setIsDisplayAllowed,
    //filesReturn//
    getIsFetchMore,
    getIsShowMore,
    getFilesLoading,
    setFilesLoading,
    setSorting,
    getSorting,
    incPageNumber,
    getPageNumber,
    resetPageNumber,
    setSearchParam,
    getSearchParam,
    setFolderStructured,
    getFolderStructured,
    setContributorsFilter,
    getContributorsFilter,
    getContributorsData,
    setContributorsData,
    resetFilesList,
    setFilesList,
    getFilesList,
    fetchMailFiles,
    getImageSrcForCanvas,
    getFilesData,
    removeFiles,
    removeFolder,
    //foldersReturn//
    fetchFoldersData,
    getFoldersData,
    getFoldersList,
    getAvailableFoldersList,
    getPathToFolder,
    setCurrentFolder,
    getCurrentFolder,
    getCurrentFolderData,
    //collectionsReturn//
    fetchCollectionsData,
    addCollection,
    editCollectionName,
    deleteCollection,
    getCollectionsData,
    getCollectionsList,
    setCurrentCollection,
    getCurrentCollection,
    addFilesToCollection,
    removeFilesFromCollection,
    isFileInCollection,
    //reuploading
    setReuploadingMail,
    getReuploadingMail,
    getFilesDataErrorMessage,
  };
};

export default galleryFactory;
